import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { FlashlightOff, FlashlightOn } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import CopyableField from '~/components/Common/CopyableField';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import ErrorBoundary from '~/components/Common/ErrorBoundary';
import { Autocomplete, Input } from '~/components/Common/Forms/fields';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import DebugModal from '~/components/DebugModal/DebugModal';
import { promoCodePath } from '~/components/Promo/promoUrls';
import { PromoRequestDeviceType } from '~/components/PromoV3/PromoRequestDeviceType';
import { PromoRequestSourceFormatter } from '~/components/PromoV3/PromoRequestSource';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import {
  PromoMeta,
  PromoRequestInputType,
  PromoRequestOutputType,
  getFilterTitle,
  getPromoRequestWarnings,
} from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import dateFormatterDetailed from '~/utils/dateFormatterDetails';
import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';
import statusCodeFormatter from '~/utils/statusCodeFormatter';

import GridPagination from '../../Common/Elements/GridPagination';
import Spinner from '../../Common/Spinner';
import { PromoResponse } from '../PromoResponse/PromoResponse';
import PromoResponseDetails from '../PromoResponse/PromoResponseDetails';
import ColumnSelector from '../PromoTester/ColumnSelector';
import PromoCodeUser from '../formatters/PromoCodeUser';
import { PromoRequestUrlState } from '../hooks/usePromoRequestUrl';
import usePromoRequests, {
  getPromoRequestDiscountBreakdownItems,
  getPromoRequestOrderDiscountAmount,
} from '../hooks/usePromoRequests';

import { PromoDiscountBreakdown } from './PromoDiscountDetails';
import { PromoOrderTableCell } from './PromoOrderTableCell';
import PromoRequestItems from './PromoRequestItems';
import PromoRequestLoyaltyItems from './PromoRequestLoyaltyItems';
import PromoRequestTypeFilters from './PromoRequestTypeFilters';
import PromoRequestVoucherLogs from './PromoRequestVoucherLogs';

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 0,
};

const ERROR_CODE_SUMMARY_MAX_LENGTH = 160;

export const getRequestKey = (createdAt: string, codeName: string) => `${createdAt}_${codeName}`;

interface PromoRequestErrorCodeProps {
  statusCode?: number | null;
  errorCode?: string;
  errorMessage?: string;
}

function PromoRequestErrorCode({ statusCode, errorCode, errorMessage }: PromoRequestErrorCodeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const canExpand = (errorMessage?.length ?? 0) > ERROR_CODE_SUMMARY_MAX_LENGTH;

  return (
    <Box sx={{ maxWidth: '100%', overflowWrap: 'anywhere' }}>
      {statusCodeFormatter({ extended: !canExpand || isExpanded }, statusCode, errorCode, errorMessage)}
      {canExpand && (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setIsExpanded((expanded) => !expanded);
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
}

function getChannelMarkupMarkupIds(request: definitions['PromoCodeRequest']): Array<string> {
  const items = request.bodyJSON?.order?.items ?? [];

  return Array.from(
    new Set(
      items.flatMap((item) => {
        const channelMarkupMarkupId = item.itemModifierInfo?.channelMarkupMarkupId;
        return channelMarkupMarkupId ? [channelMarkupMarkupId] : [];
      }),
    ),
  );
}

const defaultExpandedColumnVisibilityModel: GridColumnVisibilityModel = {
  uniqueId: false,
  warnings: false,
  bodyJSON: false,
  orderLineIds: false,
  responseJSON: false,
  responseAndRequest: true,
  limits: true,
  promoResponseDetails: false,
};

const getColumns = (meta: PromoMeta, isDev: boolean): Array<GridColDef> => [
  {
    field: 'requestAt',
    headerName: 'Request At',
    renderCell: (params) => dateFormatterDetailed({ date: params.row.createdAt }),
    ...sharedAttrs,
    flex: 0,
  },
  {
    field: 'voucherName',
    headerName: 'Voucher',
    renderCell: (params) => (
      <Stack key={params.row.uniqueKey}>
        {params.row.bodyJSON?.order?.voucherIds &&
          params.row.bodyJSON?.order?.voucherIds.length > 0 &&
          params.row.bodyJSON?.order?.voucherIds.map((voucherId: string) => (
            <CopyableField
              key={voucherId}
              value={voucherId}
              label={
                <Link href={`/marketing/vouchers/${voucherId}`} target="_blank" rel="noreferrer" color="inherit">
                  {voucherId}
                </Link>
              }
            />
          ))}
        {params.row.voucherNames &&
          params.row.voucherNames.length > 0 &&
          params.row.voucherNames.map((voucherName: string) => (
            <CopyableField key={voucherName} value={voucherName} label={voucherName} />
          ))}
      </Stack>
    ),
    ...sharedAttrs,
    flex: 1,
  },
  {
    field: 'codeName',
    headerName: 'Code Name',
    renderCell: (params) => (
      <CopyableField
        value={params.row.codeName}
        label={
          <Link href={promoCodePath(params.row.codeName)} target="_blank" rel="noreferrer" color="inherit">
            {params.row.codeName}
          </Link>
        }
      />
    ),
    ...sharedAttrs,
    flex: 1,
  },
  {
    field: 'Requested By',
    renderCell: (params) => (
      <Box>
        <PromoCodeUser userId={params.row.userId} userIdFor={params.row.userIdFor} />
        {(params.row.responseJSON?.result?.promo?.limit_per_user_per_year_remaining > 0 ||
          params.row.responseJSON?.result?.promo?.limit_per_user_per_year > 0) && (
          <Typography
            title={`A yearly limit of ${
              params.row.responseJSON?.result?.promo?.limit_per_user_per_year || -1
            } was set for this promo code, this user is at use number ${
              params.row.responseJSON?.result?.promo?.limit_per_user_per_year_remaining || -1
            }.`}
          >
            ({params.row.responseJSON?.result?.promo?.limit_per_user_per_year_remaining || -1}/
            {params.row.responseJSON?.result?.promo?.limit_per_user_per_year || -1})
          </Typography>
        )}
      </Box>
    ),
    ...sharedAttrs,
  },
  {
    field: 'statusCode',
    headerName: 'Error Code',
    minWidth: 220,
    maxWidth: 320,
    renderCell: (params) => (
      <PromoRequestErrorCode
        statusCode={params.row.statusCode}
        errorCode={params.row.errorCode}
        errorMessage={params.row.errorMsg}
      />
    ),
    ...sharedAttrs,
  },
  {
    field: 'amount',
    headerName: 'Discount',
    sortable: false,
    minWidth: 220,
    renderCell: (params) => (
      <PromoDiscountBreakdown
        currencyCode={
          params.row.bodyJSON?.order?.region
            ? getCurrencyCodeByRegionCode(params.row.bodyJSON.order.region)
            : params.row.responseJSON?.result?.promo?.currency
        }
        items={getPromoRequestDiscountBreakdownItems(params.row)}
        orderDiscountAmount={getPromoRequestOrderDiscountAmount(params.row)}
        column="amount"
      />
    ),
    ...sharedAttrs,
  },
  {
    field: 'discountableTotal',
    headerName: 'Discountable total',
    minWidth: 150,
    renderCell: (params) => (
      <PromoDiscountBreakdown
        currencyCode={
          params.row.bodyJSON?.order?.region
            ? getCurrencyCodeByRegionCode(params.row.bodyJSON.order.region)
            : params.row.responseJSON?.result?.promo?.currency
        }
        items={getPromoRequestDiscountBreakdownItems(params.row)}
        column="discountableTotal"
      />
    ),
    ...sharedAttrs,
  },
  {
    field: 'discountPercentage',
    headerName: 'Discount %',
    minWidth: 110,
    renderCell: (params) => (
      <PromoDiscountBreakdown items={getPromoRequestDiscountBreakdownItems(params.row)} column="percentage" />
    ),
    ...sharedAttrs,
  },
  {
    field: 'voucherTotal',
    headerName: 'Voucher Total',
    renderCell: (params) => {
      const voucherTotal = params.row.responseJSON?.result?.promo?.voucherTotal;
      if (voucherTotal == null || params.row.vouchersApplied?.length === 0) {
        return null;
      }
      if (voucherTotal === 0) {
        return null;
      }
      const currencyCode = params.row.responseJSON?.result?.promo?.currency;
      return currencyCode ? currencyFormatter(currencyCode, voucherTotal) : voucherTotal;
    },
    ...sharedAttrs,
  },
  {
    field: 'deviceType',
    headerName: 'Source',
    sortable: false,
    renderCell: (params) => (
      <>
        {isDev && <PromoRequestSourceFormatter requestSource={params.row.bodyJSON?.requestSource} />}
        <PromoRequestDeviceType
          deviceType={params.row.bodyJSON?.order?.deviceType}
          requestSource={params.row.bodyJSON?.requestSource}
        />
      </>
    ),
    ...sharedAttrs,
  },
  {
    field: 'region',
    headerName: 'Region',
    renderCell: (params) => {
      const promo = params.row.promo ?? params.row.responseJSON?.result?.promo;

      return (
        <Typography>
          {params.row.bodyJSON?.order?.region}
          {promo?.code_name && <PromoResponseDetails response={promo} isDev={isDev} />}
        </Typography>
      );
    },
    ...sharedAttrs,
  },
  {
    field: 'bodyJSON',
    headerName: 'Order',
    renderCell: (params) => <PromoOrderTableCell order={params.row.bodyJSON?.order} />,
    ...sharedAttrs,
    flex: 3,
  },
  {
    field: 'loyaltyOfferTypes',
    headerName: 'Loyalty Offer Type',
    description: 'If the promo items have loyalty offer types, they will be listed here',
    renderCell: (params) => {
      const items = params.row.bodyJSON?.order?.items;
      const loyaltyOfferTypeItems = Array.isArray(items) ? items.filter((item) => item.loyaltyOfferType) : [];

      return loyaltyOfferTypeItems.length > 0 ? (
        <Stack spacing={0.5}>
          {loyaltyOfferTypeItems.map((item, i) => (
            <Typography key={i} variant="body2" component="div">
              {item.loyaltyOfferType}
            </Typography>
          ))}
        </Stack>
      ) : null;
    },
  },
  {
    field: 'responseAndRequest',
    headerName: 'Response/Request',
    description: '(if successful, this is the response, if unsuccessful this is the request)',
    renderCell: (params) => {
      if (params.row.statusCode >= 200 && params.row.statusCode < 300) {
        return <PromoResponse response={params.row.responseJSON} isDev={isDev} />;
      } else {
        return <PromoOrderTableCell order={params.row.bodyJSON?.order} />;
      }
    },
    ...sharedAttrs,
    flex: 4,
  },
  {
    field: 'responseJSON',
    headerName: 'Response',
    description: '(if successful, this is the response, if unsuccessful this is the request)',
    renderCell: (params) => {
      return <PromoResponse response={params.row.responseJSON} isDev={isDev} />;
    },
    ...sharedAttrs,
    flex: 3,
  },
  {
    field: 'warnings',
    headerName: 'Warnings',
    renderCell: (params) => {
      const warns = getPromoRequestWarnings(params.row);
      if (warns.length > 0) {
        return (
          <Stack direction="column">
            {warns.map((w, i) => (
              <Alert key={i} severity={w.severity}>
                {w.message}
              </Alert>
            ))}
          </Stack>
        );
      }
      return null;
    },
    ...sharedAttrs,
  },
  {
    field: 'debug',
    headerName: '',
    renderCell: (params) => (
      <CopyableField
        value={JSON.stringify(params.row, null, 4)}
        label={
          <DebugModal
            title={`v${params.row.bodyJSON?.order?.clientOrderVersion || 0}`}
            type="generic"
            data={params.row ?? { data: 'None' }}
          />
        }
      />
    ),
    ...sharedAttrs,
    flex: 0,
  },
  {
    field: 'channelMarkupMarkupId',
    headerName: 'Channel Markup Markup ID',
    minWidth: 240,
    valueGetter: (_value, row) => getChannelMarkupMarkupIds(row).join(', '),
    renderCell: (params) => {
      const channelMarkupMarkupIds = getChannelMarkupMarkupIds(params.row);

      return channelMarkupMarkupIds.length > 0 ? (
        <Stack spacing={0.5}>
          {channelMarkupMarkupIds.map((channelMarkupMarkupId) => (
            <CopyableField key={channelMarkupMarkupId} value={channelMarkupMarkupId} label={channelMarkupMarkupId} />
          ))}
        </Stack>
      ) : null;
    },
    ...sharedAttrs,
  },
];

interface Props {
  initCodeName?: string;
  initUserId?: string;
  initErrorCode?: string;
  initPage?: number;
  initLimit?: number;
  brand: App.Brands | string;
  handleUserIdFilterSet?: (userId: string) => void;
  promoMeta: PromoMeta;
  isPromoMetaLoading: boolean;
  defaultExpanded?: boolean;
  // URL management props
  urlState?: PromoRequestUrlState;
  updateUrlState?: (newState: Partial<PromoRequestUrlState>) => void;
  isDevMode?: boolean;
  setIsDevMode?: (isDev: boolean) => void;
  isExpanded?: boolean;
  setIsExpanded?: (expanded: boolean) => void;
  isItemsExpanded?: boolean;
  setIsItemsExpanded?: (expanded: boolean) => void;
  isLoyaltyItemsExpanded?: boolean;
  setIsLoyaltyItemsExpanded?: (expanded: boolean) => void;
  isVoucherLogsExpanded?: boolean;
  setIsVoucherLogsExpanded?: (expanded: boolean) => void;
  isLogsExpanded?: boolean;
  setIsLogsExpanded?: (expanded: boolean) => void;
  includeVoucherLogs?: boolean;
  defaultVisibleItemFields?: Array<string>;
  onDebugDataChange?: (data: {
    promoRequests: Array<definitions['PromoCodeRequest'] & { uniqueKey: string }>;
    promoRequestItems: Array<unknown>;
    promoRequestLoyaltyItems: Array<unknown>;
    totalRows: number;
    promoRequestsFilter: unknown;
  }) => void;
}

function PromoRequests({
  initCodeName,
  initUserId,
  initErrorCode,
  initPage,
  initLimit,
  brand,
  handleUserIdFilterSet,
  promoMeta,
  isPromoMetaLoading,
  defaultExpanded = false,
  // URL management props
  urlState,
  updateUrlState,
  isDevMode,
  setIsDevMode,
  isExpanded,
  setIsExpanded,
  isItemsExpanded,
  setIsItemsExpanded,
  isLoyaltyItemsExpanded,
  setIsLoyaltyItemsExpanded,
  isVoucherLogsExpanded,
  setIsVoucherLogsExpanded,
  isLogsExpanded,
  setIsLogsExpanded,
  includeVoucherLogs = true,
  defaultVisibleItemFields = [],
  onDebugDataChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const resolvedExpanded = isExpanded ?? uncontrolledExpanded;

  // Column visibility state for PromoRequests
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
    // When isDevMode is true, show all columns
    if (isDevMode) {
      return {
        requestAt: true,
        codeName: true,
        'Requested By': true,
        amount: true,
        statusCode: true,
        channelMarkupMarkupId: true,
        deviceType: true,
        region: true,
        bodyJSON: true,
        orderLineIds: true,
        responseAndRequest: true,
        responseJSON: true,
        warnings: true,
        debug: true,
      };
    }

    // Default visibility for non-dev mode
    return defaultExpandedColumnVisibilityModel;
  });

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  // Update column visibility when isDevMode changes
  useEffect(() => {
    if (isDevMode) {
      setColumnVisibilityModel({
        requestAt: true,
        codeName: true,
        'Requested By': true,
        amount: true,
        statusCode: true,
        channelMarkupMarkupId: true,
        deviceType: true,
        region: true,
        bodyJSON: true,
        orderLineIds: true,
        responseAndRequest: true,
        responseJSON: true,
        warnings: true,
        debug: true,
      });
    } else {
      setColumnVisibilityModel(defaultExpandedColumnVisibilityModel);
    }
  }, [isDevMode]);

  // Use URL state from props if provided, otherwise fall back to init props
  const effectiveUrlState = urlState || {
    codeName: initCodeName,
    voucherName: undefined,
    userId: initUserId,
    errorCode: initErrorCode,
    requestSource: undefined,
    inputType: undefined,
    outputType: undefined,
    page: initPage,
    limit: initLimit,
  };

  const {
    promoRequests,
    promoRequestItems,
    promoRequestLoyaltyItems,
    totalRows,
    isLoading,
    error,
    promoRequestsFilter,
    setPromoRequestsFilter,
  } = usePromoRequests({
    initPage: effectiveUrlState.page ?? 0,
    initLimit: effectiveUrlState.limit ?? 10,
    initErrorCode: effectiveUrlState.errorCode,
    initCodeName: effectiveUrlState.codeName,
    initVoucherName: effectiveUrlState.voucherName,
    brand: brand.toString(),
    initUserId: effectiveUrlState.userId,
    initRequestSource: effectiveUrlState.requestSource,
    initInputType: effectiveUrlState.inputType,
    initOutputType: effectiveUrlState.outputType,
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Promo Request Error: ${error.message}`, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [error, enqueueSnackbar]);

  // Hide codeName column when filtering by voucherName
  useEffect(() => {
    const isFilteredByVoucher = !!promoRequestsFilter.voucherName;
    setColumnVisibilityModel((prev) => ({
      ...prev,
      codeName: isFilteredByVoucher ? false : (prev.codeName ?? true),
    }));
  }, [promoRequestsFilter.voucherName]);

  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      codeName: effectiveUrlState.codeName ?? '',
      voucherName: effectiveUrlState.voucherName ?? '',
      userId: effectiveUrlState.userId ?? '',
      errorCode: effectiveUrlState.errorCode ?? '',
    },
  });

  // Update form values when URL state changes
  useEffect(() => {
    setValue('codeName', effectiveUrlState.codeName ?? '');
    setValue('voucherName', effectiveUrlState.voucherName ?? '');
    setValue('userId', effectiveUrlState.userId ?? '');
    setValue('errorCode', effectiveUrlState.errorCode ?? '');
  }, [
    effectiveUrlState.codeName,
    effectiveUrlState.voucherName,
    effectiveUrlState.userId,
    effectiveUrlState.errorCode,
    setValue,
  ]);

  // Update promo requests filter when URL state changes
  useEffect(() => {
    if (
      effectiveUrlState.page !== undefined ||
      effectiveUrlState.limit !== undefined ||
      effectiveUrlState.codeName !== undefined ||
      effectiveUrlState.voucherName !== undefined ||
      effectiveUrlState.userId !== undefined ||
      effectiveUrlState.errorCode !== undefined ||
      effectiveUrlState.requestSource !== undefined ||
      effectiveUrlState.inputType !== undefined ||
      effectiveUrlState.outputType !== undefined
    ) {
      setPromoRequestsFilter((prev) => ({
        ...prev,
        page: effectiveUrlState.page ?? 0,
        limit: effectiveUrlState.limit ?? 10,
        codeName: effectiveUrlState.codeName,
        voucherName: effectiveUrlState.voucherName,
        userId: effectiveUrlState.userId,
        errorCode: effectiveUrlState.errorCode,
        requestSource: effectiveUrlState.requestSource,
        inputType: effectiveUrlState.inputType,
        outputType: effectiveUrlState.outputType,
      }));
    }
  }, [
    effectiveUrlState.page,
    effectiveUrlState.limit,
    effectiveUrlState.codeName,
    effectiveUrlState.voucherName,
    effectiveUrlState.userId,
    effectiveUrlState.errorCode,
    effectiveUrlState.requestSource,
    effectiveUrlState.inputType,
    effectiveUrlState.outputType,
    setPromoRequestsFilter,
  ]);

  const onSubmit = async (formData) => {
    try {
      setPromoRequestsFilter({
        ...promoRequestsFilter,
        ...formData,
      });

      if (formData.voucherName) {
        setIsItemsExpanded?.(false);
        setIsLoyaltyItemsExpanded?.(false);
        setIsLogsExpanded?.(false);
      }

      if (updateUrlState) {
        updateUrlState(formData);
      }
    } catch (err) {
      enqueueSnackbar(err.message || 'Error! Unable to update filters.', { variant: 'error' });
    }
  };

  const onPageChange = async (page, pageSize) => {
    const formData = getValues();
    setPromoRequestsFilter((prev) => ({
      ...prev,
      page,
      limit: pageSize,
      brand: brand as definitions['Promo Brands'],
      ...formData,
    }));
    if (updateUrlState) {
      updateUrlState({ page, limit: pageSize });
    }
  };

  const fetchUser = useCallback(
    async (userId: string) => {
      setValue('userId', userId);
    },
    [brand, enqueueSnackbar],
  );

  const handleSetDev = () => {
    if (setIsDevMode) {
      setIsDevMode(!isDevMode);
    }
  };

  const handleUserSearchSelect = useCallback(
    (id, searchQuery: string) => {
      fetchUser(searchQuery);
    },
    [fetchUser],
  );

  const errorOptions: Array<string> = Array.from(
    new Set(promoMeta?.discount_error_codes.map((c) => c.toString()).concat('')),
  );

  const requestSourceSelectOptions = useMemo(() => {
    const raw = promoMeta?.request_source_options ?? [];
    const forAdmin = raw.filter((o) => o.showInAdmin);
    return (forAdmin.length > 0 ? forAdmin : raw).map((o) => o.key).sort((a, b) => a.localeCompare(b));
  }, [promoMeta?.request_source_options]);

  const showRequestSourceFilter = requestSourceSelectOptions.length > 0;

  const handleRequestSourceChange = useCallback(
    (value: string) => {
      setPromoRequestsFilter((prev) => ({
        ...prev,
        requestSource: value || undefined,
        page: 0,
      }));
      updateUrlState?.({ requestSource: value || undefined, page: 0 });
    },
    [setPromoRequestsFilter, updateUrlState],
  );

  const handleInputTypeChange = useCallback(
    (inputType?: Array<PromoRequestInputType>) => {
      setPromoRequestsFilter((prev) => ({ ...prev, inputType, page: 0 }));
      updateUrlState?.({ inputType, page: 0 });
    },
    [setPromoRequestsFilter, updateUrlState],
  );

  const handleOutputTypeChange = useCallback(
    (outputType?: Array<PromoRequestOutputType>) => {
      setPromoRequestsFilter((prev) => ({ ...prev, outputType, page: 0 }));
      updateUrlState?.({ outputType, page: 0 });
    },
    [setPromoRequestsFilter, updateUrlState],
  );

  const [highlightedRows, setHighlightedRows] = useState<Array<string>>([]);

  const filterTitle = getFilterTitle({
    prefix: `Recent Requests (${totalRows})`,
    codeName: promoRequestsFilter.codeName,
    userId: promoRequestsFilter.userId,
  });

  useEffect(() => {
    const promoRequestsWithKeys = (promoRequests ?? []).map((request, index) => ({
      ...request,
      uniqueKey: (request as { uniqueKey?: string }).uniqueKey ?? `${request.userIdFor}-${request.createdAt}-${index}`,
    }));

    onDebugDataChange?.({
      promoRequests: promoRequestsWithKeys,
      promoRequestItems: promoRequestItems ?? [],
      promoRequestLoyaltyItems: promoRequestLoyaltyItems ?? [],
      totalRows,
      promoRequestsFilter,
    });
  }, [onDebugDataChange, promoRequests, promoRequestItems, promoRequestLoyaltyItems, totalRows, promoRequestsFilter]);

  const handleRowClick = (requestId: string) => {
    setHighlightedRows((prev) =>
      prev.includes(requestId) ? prev.filter((id) => id !== requestId) : [...prev, requestId],
    );
  };

  return (
    <>
      <Box m={2} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2} alignItems="center">
          <UserSearchWidget
            id="fk_customer_id"
            label="Name / Email / Customer Support Code / User ID"
            formErrors={[]}
            loading={false}
            onChange={handleUserSearchSelect}
          />
          <Grid container spacing={2} sx={{ width: '100%' }} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Input fullWidth control={control} muiLabel="Code Name" name="codeName" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Input fullWidth control={control} muiLabel="Voucher Name" name="voucherName" />
            </Grid>
            {promoMeta ? (
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Autocomplete control={control} options={errorOptions} name="errorCode" muiLabel="Error Code" />
              </Grid>
            ) : null}
            {showRequestSourceFilter ? (
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="promo-requests-request-source-label">Request source</InputLabel>
                  <Select
                    labelId="promo-requests-request-source-label"
                    id="promo-requests-request-source"
                    value={promoRequestsFilter.requestSource ?? ''}
                    label="Request source"
                    onChange={(e) => handleRequestSourceChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {requestSourceSelectOptions.map((key) => (
                      <MenuItem key={key} value={key}>
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={4} lg="auto">
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button variant="contained" color="primary" type="submit">
                  Search
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetDev}
                  sx={{ padding: '0 16px' }}
                  title={isDevMode ? 'Hide extended info' : 'Show extended info'}
                >
                  {isDevMode ? <FlashlightOn /> : <FlashlightOff />}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      <ErrorBoundary fallbackTitle="Recent Requests">
        <Accordion
          disableGutters
          expanded={resolvedExpanded}
          onChange={(_, expanded) => {
            if (setIsExpanded) {
              setIsExpanded(expanded);
            } else {
              setUncontrolledExpanded(expanded);
            }
          }}
        >
          <AccordionSummary
            expandIcon={<GridExpandMoreIcon />}
            aria-controls="offer-fields"
            id="offer-fields"
            sx={{
              backgroundColor: 'action.hover',
              color: 'text.primary',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <PageSubheader title={filterTitle} />
          </AccordionSummary>
          <AccordionDetails>
            <PromoRequestTypeFilters
              inputTypes={promoRequestsFilter.inputType}
              outputTypes={promoRequestsFilter.outputType}
              onInputTypesChange={handleInputTypeChange}
              onOutputTypesChange={handleOutputTypeChange}
            />
            {isLoading || isPromoMetaLoading ? (
              <Spinner size={36} />
            ) : (
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                  <ColumnSelector
                    columns={getColumns(promoMeta, isDevMode)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                  />
                </Box>
                <DataGrid
                  columns={getColumns(promoMeta, isDevMode)}
                  autoHeight
                  getRowId={(row) => row.uniqueKey}
                  rowCount={totalRows}
                  rows={promoRequests || []}
                  pagination
                  paginationMode="server"
                  paginationModel={{ page: promoRequestsFilter.page, pageSize: promoRequestsFilter.limit }}
                  slots={{ pagination: GridPagination }}
                  pageSizeOptions={DEFAULT_PAGE_SIZES}
                  onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
                  getRowHeight={() => 'auto'}
                  getRowClassName={(params) => {
                    const requestKey = getRequestKey(params.row.requestAt, params.row.codeName);
                    return highlightedRows.includes(requestKey) ? 'highlighted-row' : '';
                  }}
                  sx={{
                    '& .highlighted-row': {
                      backgroundColor: 'action.selected',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    },
                  }}
                  onRowClick={(params) => {
                    const rowId = params.id.toString();
                    setHighlightedRows((prev) =>
                      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId],
                    );
                  }}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </ErrorBoundary>
      <ErrorBoundary fallbackTitle="Promo Request Items">
        <PromoRequestItems
          requestType="Request Related"
          meta={promoMeta}
          promoRequestItems={promoRequestItems || []}
          isLoading={isLoading}
          highlightedRows={highlightedRows}
          onRowClick={(params) => handleRowClick(getRequestKey(params.requestAt, params.codeName))}
          expanded={isItemsExpanded}
          onExpandedChange={setIsItemsExpanded && setIsItemsExpanded}
          promoRequestsFilter={promoRequestsFilter}
          setPromoRequestsFilter={setPromoRequestsFilter}
          updateUrlState={updateUrlState}
          setFormUserId={(userId) => setValue('userId', userId)}
          hideRequestSourceFilter
          inputTypes={promoRequestsFilter.inputType}
          outputTypes={promoRequestsFilter.outputType}
          onInputTypesChange={handleInputTypeChange}
          onOutputTypesChange={handleOutputTypeChange}
          defaultVisibleItemFields={defaultVisibleItemFields}
        />
      </ErrorBoundary>
      <ErrorBoundary fallbackTitle="Promo Loyalty Items">
        <PromoRequestLoyaltyItems
          meta={promoMeta}
          promoRequestLoyaltyItems={promoRequestLoyaltyItems || []}
          isLoading={isLoading}
          highlightedRows={highlightedRows}
          // onRowClick={(params) => handleRowClick(params.loyaltyProductType)}
          expanded={isLoyaltyItemsExpanded}
          onExpandedChange={setIsLoyaltyItemsExpanded && setIsLoyaltyItemsExpanded}
          promoRequestsFilter={promoRequestsFilter}
          setPromoRequestsFilter={setPromoRequestsFilter}
          updateUrlState={updateUrlState}
          setFormUserId={(userId) => setValue('userId', userId)}
        />
      </ErrorBoundary>
      {includeVoucherLogs && (
        <ErrorBoundary fallbackTitle="Promo Voucher Logs">
          <PromoRequestVoucherLogs
            expanded={isVoucherLogsExpanded}
            onExpandedChange={setIsVoucherLogsExpanded}
            voucherNames={promoRequestsFilter.voucherName ? [promoRequestsFilter.voucherName] : undefined}
            userId={promoRequestsFilter.userId}
            brand={promoRequestsFilter.brand}
            promoRequestsFilter={promoRequestsFilter}
            setPromoRequestsFilter={setPromoRequestsFilter}
            updateUrlState={updateUrlState}
            setFormUserId={(userId) => setValue('userId', userId)}
            setFormVoucherName={(voucherName) => setValue('voucherName', voucherName)}
          />
        </ErrorBoundary>
      )}
    </>
  );
}

export default PromoRequests;
